import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../components/Modal';
import { backendErrorNotification } from '../../../../components/Notification';
import HttpStatus from '../../../../enums/httpStatus';
import { messageApi } from '../../../../services/requests';
import { Message, MessageSeller } from '../../../../types/apiResponse/message';
import * as s from './styles';

type MessageDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  messageId: number | null;
};

const MessageDetailsModal = ({ open, onClose, messageId }: MessageDetailsModalProps) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const statusMap = useMemo<
    Record<'s' | 'n', { label: string; variant: 'positive' | 'warning' }>
  >(
    () => ({
      s: {
        label: t('yes'),
        variant: 'positive',
      },
      n: {
        label: t('no'),
        variant: 'warning',
      },
    }),
    [i18n.language, t],
  );

  useEffect(() => {
    if (!open) {
      setMessage(null);
    }
  }, [open]);

  useEffect(() => {
    const fetchMessage = async () => {
      if (!open || !messageId) {
        return;
      }

      setIsLoading(true);

      try {
        messageApi.query = '';
        const response = await messageApi.show(messageId);

        if (response.status !== HttpStatus.OK) {
          throw response;
        }

        const { data } = response.data as { data: Message };
        setMessage(data);
      } catch (error) {
        backendErrorNotification(error as AxiosError);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessage();
  }, [open, messageId]);

  const sellers = useMemo(() => {
    if (!message?.sellers) {
      return [] as MessageSeller[];
    }

    return [...message.sellers].sort((a, b) => {
      return (a.seller?.Nome || '').localeCompare(b.seller?.Nome || '');
    });
  }, [message]);

  const createdAt = useMemo(() => {
    if (!message?.DataCadastro) {
      return '-';
    }

    const time = message.HoraCadastro ? ` ${message.HoraCadastro}` : '';
    return `${message.DataCadastro}${time}`;
  }, [message]);

  return (
    <Modal
      title={t('messageDetails')}
      showModal={open}
      closeModal={onClose}
      onlyCloseButton
      isLoading={isLoading}
      bodyStyle={{ width: '100%', overflow: 'auto' }}
    >
      {message ? (
        <s.DetailsWrapper>
          <s.InfoGrid>
            <s.InfoItem>
              <s.InfoLabel>{t('subject')}</s.InfoLabel>
              <s.InfoValue>{message.Assunto}</s.InfoValue>
            </s.InfoItem>
            <s.InfoItem>
              <s.InfoLabel>{t('registrationOrigin')}</s.InfoLabel>
              <s.InfoValue>{message.registration_origin ?? '-'}</s.InfoValue>
            </s.InfoItem>
            <s.InfoItem>
              <s.InfoLabel>{t('createdAt')}</s.InfoLabel>
              <s.InfoValue>{createdAt}</s.InfoValue>
            </s.InfoItem>
          </s.InfoGrid>

          <div>
            <s.InfoLabel>{t('message')}</s.InfoLabel>
            <s.MessageContent>{message.Mensagem}</s.MessageContent>
          </div>

          <div>
            <s.SectionTitle>{t('recipients')}</s.SectionTitle>
            <s.SellersTable>
              <thead>
                <tr>
                  <th>{t('sellerCode')}</th>
                  <th>{t('sellerName')}</th>
                  <th>{t('sent')}</th>
                  <th>{t('read')}</th>
                </tr>
              </thead>
              <tbody>
                {sellers.length === 0 && (
                  <tr>
                    <td colSpan={4}>{t('noRecipientsFound')}</td>
                  </tr>
                )}
                {sellers.map((seller) => (
                  <tr key={`${seller.CodigoMensagem}-${seller.CodigoVendedor}`}>
                    <td>{seller.CodigoVendedor}</td>
                    <td>{seller.seller?.Nome ?? '-'}</td>
                    <td>
                      <s.StatusBadge variant={statusMap[seller.FlagEnviado].variant}>
                        {statusMap[seller.FlagEnviado].label}
                      </s.StatusBadge>
                    </td>
                    <td>
                      <s.StatusBadge variant={statusMap[seller.FlagLida].variant}>
                        {statusMap[seller.FlagLida].label}
                      </s.StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </s.SellersTable>
          </div>
        </s.DetailsWrapper>
      ) : (
        <s.EmptyState>{t('loadingMessageDetail')}</s.EmptyState>
      )}
    </Modal>
  );
};

export default MessageDetailsModal;
